import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { ReCaptchaV3Service } from 'ngx-captcha'
import { EMPTY, Observable, catchError, from, of, switchMap, tap } from 'rxjs'

import { environment } from '../../../../../environments/environment'
import { AuthService } from '../../../../services/auth/auth.service'
import { UsersService } from '../../../../services/user/users.service'
import { AuthBaseFormComponent } from '../../../auth/components/auth-base-form/auth-base-form.component'
import { CreateAccountFieldsComponent } from '../../../auth/components/create-account-fields/create-account-fields.component'
import { getArticleIdFromRoute } from '../../../auth/util/article-purchase-route.utils'
import type { Requirement } from '../../../auth/util/validators'
import {
  equalValidator,
  hasError,
  passwordPattern,
  reactiveRequirements,
} from '../../../auth/util/validators'
import { ArticlePurchaseAccountShellComponent } from '../article-purchase-account-shell.component'

const requiredValidator = (control: AbstractControl) =>
  Validators.required(control)
const emailValidator = (control: AbstractControl) => Validators.email(control)

@Component({
  selector: 'dhp-article-purchase-account-register',
  templateUrl: './article-purchase-account-register.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    AuthBaseFormComponent,
    CreateAccountFieldsComponent,
    ArticlePurchaseAccountShellComponent,
  ],
})
export class ArticlePurchaseAccountRegisterComponent {
  private readonly usersService = inject(UsersService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly authService = inject(AuthService)
  private readonly reCaptchaV3Service = inject(ReCaptchaV3Service)

  readonly articleId = computed(() => getArticleIdFromRoute(this.route))
  readonly checkoutUrl = computed(() => {
    const articleId = this.articleId()

    return articleId !== null
      ? `/articles/${String(articleId)}/purchase`
      : '/articles'
  })
  readonly recaptchaSiteKey = environment.recaptchaSiteKey
  readonly creationFailed = signal(false)
  readonly form = new FormGroup<{
    first_name: FormControl<string | null>
    last_name: FormControl<string | null>
    username: FormControl<string | null>
    password: FormControl<string | null>
    confirmPassword: FormControl<string | null>
  }>(
    {
      first_name: new FormControl<string | null>(null, [requiredValidator]),
      last_name: new FormControl<string | null>(null, [requiredValidator]),
      username: new FormControl<string | null>(null, [
        requiredValidator,
        emailValidator,
      ]),
      password: new FormControl<string | null>(null, [
        requiredValidator,
        Validators.minLength(8),
        Validators.pattern(passwordPattern),
      ]),
      confirmPassword: new FormControl<string | null>(null, [
        requiredValidator,
        Validators.minLength(8),
        Validators.pattern(passwordPattern),
      ]),
    },
    { validators: equalValidator('password', 'confirmPassword') }
  )
  readonly requirements: Requirement[] = reactiveRequirements(this.form, [
    { label: 'length', isValid: () => this.isValidLength() },
    { label: 'specialCharacter', isValid: () => this.isValidPattern() },
    { label: 'match', isValid: () => this.isValidEquality() },
  ])

  constructor() {
    this.authService.currentUser$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(user => {
          if (user === undefined || !user) {
            return
          }

          if (user.managed) {
            void this.router.navigate([
              '/articles',
              this.articleId(),
              'purchase',
              'account',
              'update',
            ])
            return
          }

          if (user._verified === true) {
            void this.router.navigateByUrl(this.checkoutUrl())
            return
          }

          void this.router.navigate(
            ['/articles', this.articleId(), 'purchase', 'account', 'verify'],
            {
              queryParams: {
                email: user.email ?? undefined,
                mode: 'update',
              },
            }
          )
        })
      )
      .subscribe()
  }

  createAccount() {
    return (_formGroup: FormGroup) => {
      const firstName = this.form.controls.first_name.value ?? ''
      const lastName = this.form.controls.last_name.value ?? ''
      const email = this.form.controls.username.value ?? ''
      const password = this.form.controls.password.value ?? ''
      const articleToPurchase = this.articleId() ?? undefined
      const name = [firstName, lastName].join(' ')

      this.creationFailed.set(false)

      const createUser$ = this.recaptchaSiteKey
        ? this.executeRecaptcha('create_article_purchase_account').pipe(
            switchMap(recaptchaToken =>
              this.usersService.createUser(
                email,
                password,
                name,
                firstName,
                lastName,
                recaptchaToken,
                articleToPurchase
              )
            )
          )
        : this.usersService.createUser(
            email,
            password,
            name,
            firstName,
            lastName,
            undefined,
            articleToPurchase
          )

      return createUser$.pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.creationFailed.set(true)
          return EMPTY
        }),
        switchMap(result =>
          from(
            this.router.navigate(
              ['/articles', this.articleId(), 'purchase', 'account', 'verify'],
              {
                queryParams: {
                  email,
                  mode: 'create',
                },
                state: {
                  email,
                  mode: 'create',
                  organization: result?.organization?.name,
                },
              }
            )
          ).pipe(
            catchError((error: unknown) => {
              console.error(
                'Navigation after purchase account creation failed',
                error
              )
              return of(false)
            })
          )
        )
      )
    }
  }

  navigateToLogin(): void {
    void this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: this.checkoutUrl() },
    })
  }

  isValidLength(): boolean {
    const passwordControl = this.form.controls.password

    return (
      passwordControl.dirty &&
      !(
        hasError(passwordControl, 'minlength') ||
        hasError(passwordControl, 'required')
      )
    )
  }

  isValidPattern(): boolean {
    const passwordControl = this.form.controls.password

    return (
      passwordControl.dirty &&
      !(
        hasError(passwordControl, 'pattern') ||
        hasError(passwordControl, 'required')
      )
    )
  }

  isValidEquality(): boolean {
    const passwordControl = this.form.controls.password

    return (
      passwordControl.dirty &&
      !(
        hasError(passwordControl, 'equal') ||
        hasError(passwordControl, 'required')
      )
    )
  }

  private executeRecaptcha(action: string): Observable<string> {
    return new Observable<string>(observer => {
      try {
        this.reCaptchaV3Service.execute(
          this.recaptchaSiteKey,
          action,
          token => {
            if (observer.closed) {
              return
            }

            if (!token) {
              observer.error(new Error('reCAPTCHA token missing'))
              return
            }

            observer.next(token)
            observer.complete()
          },
          {
            useGlobalDomain: false,
          }
        )
      } catch (err: unknown) {
        if (!observer.closed) {
          observer.error(err instanceof Error ? err : new Error(String(err)))
        }
      }
    })
  }
}
